<template>
    <div class="row justify-center">
        <div style="width: 100%;">
            <q-card-section class="q-pa-none">
                <q-inner-loading style="z-index: 999" :showing="loading">
                    <q-spinner-comment size="100px" color="primary" />
                </q-inner-loading>

                <q-scroll-area ref="chatArea" style="width: 100%; height: calc(100vh - 260px);">
                    <q-scroll-observer @scroll="scrollHandler"/>
                    <q-resize-observer @resize="resizeHandler"/>
                    <q-chat-message
                        v-for="(note, index) in notes"
                        class="q-pa-sm q-px-md"
                        :name="sender(note)"
                        :stamp="note.created_at"
                        :avatar="note.profile_photo"
                        :key="index"
                        :sent="!note.sent"
                        text-color="black"
                        :bg-color="!note.sent ? 'deep-purple-1' : 'grey-3'"
                    >
                        <div @dblclick="edit(note)" v-html="note.content.replace(/\n/g, '<br>')" :style="isFirstCharASCII(note.content) ? '' : 'direction: rtl'"/>
                        <template v-slot:name>
                            <div class="row" :class="{'reverse': !note.sent}">
                                <q-btn
                                    flat round dense
                                    color="primary"
                                    icon="fa fa-ellipsis-v"
                                    size="xs"
                                    class="q-ml-xs"
                                    v-if="!note.sent || $auth.can('notes.edit')"
                                >
                                    <q-menu>
                                        <q-list style="min-width: 100px">
                                            <q-item clickable v-close-popup @click="edit(note)" class="q-py-none" v-if="!note.sent">
                                                <q-item-section>
                                                    <div class="text-orange"><q-icon name="fa fa-edit"/> Edit </div>
                                                </q-item-section>
                                            </q-item>
                                            <q-item clickable v-close-popup @click="destroy(note)" class="q-py-none" v-if="!note.sent || $auth.can('notes.edit')">
                                                <q-item-section>
                                                    <div class="text-red"><q-icon name="fa fa-trash"/> Delete </div>
                                                </q-item-section>
                                            </q-item>
                                        </q-list>
                                    </q-menu>
                                </q-btn>
                                <span class="text-grey">{{note.name}}</span>
                            </div>
                        </template>
                        <template v-slot:stamp>
                            <q-badge
                                outline
                                color="primary"
                                v-for="tag in note.tags" :key="tag"
                                :label="tag"
                                class="q-mr-xs"
                            />
                            <div class="q-message-stamp">{{note.created_at}}</div>
                        </template>
                    </q-chat-message>
                </q-scroll-area>
            </q-card-section>

            <q-separator class="q-mb-md"/>

            <q-card-section class="q-pa-none">
                <q-field class="q-px-md" borderless>
                    <template v-slot:control>
                        <q-editor
                            v-model="inputNote.content"
                            min-height="5rem"
                            :toolbar="toolbar"
                            :definitions="{
                                edit: {
                                  tip: 'Cancel Editing',
                                  icon: 'fa fa-times',
                                  label: 'Editing ...',
                                  handler: cancelEditing
                                },
                              }"
                            autofocus
                            @keyup.enter.stop
                            class="full-width"
                            :content-style="isFirstCharASCII(inputNote.content) ? {} : {direction: 'rtl', textAlign: 'right'}"
                        />
                    </template>

                    <template v-slot:after>
                        <q-btn round dense flat icon="send" @click="send"/>
                    </template>
                </q-field>
            </q-card-section>
        </div>
    </div>
</template>
<script>

import Note from "../models/Note"
import { mapGetters } from 'vuex'
import profileDetailsForm from "Modules/User/resources/pages/account/identity_states/kyc_forms/ProfileDetailsForm.vue"

export default {
    name: "ChatBox",
    props: {
        noteableType: {
            type: String,
            required: true
        },
        noteableId: {
            type: [Number, String],
            required: true
        },
    },
    computed: {
        profileDetailsForm() {
            return profileDetailsForm
        },
        ...mapGetters(['currentUser']),
        filterQueryString() {
            let user_id = `id=${this.noteableId}`

            return `${user_id}`
        },
        toolbar() {
            return this.inputNote.id ? [['bold', 'italic', 'underline'], ['edit']] : [['bold', 'italic', 'underline']]
        },
        userIconPath() {
            return this.currentUser.gender === 'F' ? 'images/user-female.png' : '/images/user-icon.png'
        }
    },
    data() {
        return {
            loading: false,
            inputNote: this.initiate(),
            notes: [],
            serverPagination: {
                page: 1,
                sortBy: 'id',
                rowsPerPage: 15,
                descending: true,
                rowsNumber: 10
            },
            currentPage: 1,
            lastPage: 1,
            lastChatSize: 0,
            editMode: false
        }
    },
    methods: {
        initiate() {
            return new Note({
                noteable_type: this.noteableType,
                noteable_id: this.noteableId,
                content: '',
            })
        },
        all() {
            let serverQuery = {
                page: this.currentPage,
                noteable_type: this.noteableType,
                noteable_id: this.noteableId,
            }
            this.loading = true
            serverQuery = new URLSearchParams(serverQuery).toString();
            Note.all(serverQuery, ({data, meta}) => {
                if (this.currentPage === 1) {
                    this.notes = []
                }

                let old_notes = data.reverse()
                this.notes.unshift(...old_notes)

                this.currentPage = meta.current_page
                this.lastPage = meta.last_page

                this.loading = false
            }).catch(error => {
                this.$q.notify({
                    message: error.data.error,
                    color: 'negative',
                    icon: 'fa fa-exclamation-triangle'
                })
                this.loading = false
            })
        },
        scrollHandler(details) {
            if (details.direction === 'up' && details.position === 0 && this.currentPage !== this.lastPage) {
                this.currentPage += 1
                this.loading = true
                this.all()
            }
        },
        resizeHandler(size) {
            if (this.currentPage === 1) {
                this.lastChatSize = size.height
                this.$refs.chatArea.setScrollPosition(this.lastChatSize)
            } else {
                this.$refs.chatArea.setScrollPosition(size.height - this.lastChatSize - 50)
                this.lastChatSize = size.height
            }
        },
        sender(note) {
            if (this.$auth.can('users.list')) {
                return `<a href='/users?q=` + note.email + `' style='text-decoration: none;color:black'>` + note.name + `</a>`
            } else {
                return note.name
            }
        },
        make() {
            this.notes.push({
                content: this.inputNote.content,
                sent: false,
                email: this.currentUser.email,
                name:  this.currentUser.first_name,
                created_at: 'now',
                profile_photo: this.currentUser.profile_photo ? this.currentUser.profile_photo : this.userIconPath,
            })
            const note = this.inputNote.content

            this.inputNote.create(data => {
                this.all()
                this.currentPage = 1
                this.inputNote.noteable_type = this.noteableType
                this.inputNote.noteable_id = this.noteableId
                this.$emit('made', note)
            }).catch(({data, status}) => {
                this.$q.notify({
                    message: data.error,
                    color: 'negative',
                    icon: 'fa fa-exclamation-triangle'
                })
                this.loading = false
            })
        },
        update() {
            let note = this.notes.find(note => note.id === this.inputNote.id)
            note.content = this.inputNote.content

            new Note({
                id: this.inputNote.id,
                content: this.inputNote.content
            }).update((data) => {
                this.all()
                this.$emit('edited', note.content)
            })
        },
        send() {
            if (this.inputNote.id) {
                this.update()
            } else {
                this.make()
            }
            this.inputNote = this.initiate()
        },
        edit(note) {
            if (note.sent) {
                return
            }
            this.editMode = true
            this.inputNote = note
        },
        cancelEditing() {
            this.inputNote = this.initiate()
        },
        destroy(note) {
            this.$q.dialog({
                title: 'Delete note',
                message: 'Are you sure?',
                ok: 'Yes',
                cancel: 'No'
            }).onOk(() => {
                this.notes = this.notes.filter(note_item => note_item.id !== note.id)
                new Note({
                    id: note.id,
                    content: this.inputNote.content
                }).destroy((data) => {
                    this.all()
                })
            })
        },
        isFirstCharASCII(str) {
            if (str.length === 0) {
                return false
            }
            const firstChar = str.charAt(0)
            const code = firstChar.charCodeAt(0)
            return code >= 0 && code <= 127
        }
    },
    created() {},
    mounted() {
        this.all()
    },
    watch: {},
    components: {},
}
</script>
<style>

</style>
